import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mic, Square, Play, Pause, Trash2, Volume2, Sparkles, 
  Check, MicOff, X, Loader2, FileText, WifiOff, Upload,
  Maximize2, Minimize2, BookOpen, PenLine, Clock, Hash,
  ChevronLeft, ChevronRight, Lightbulb, Users, XCircle,
  ImageIcon,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { useOffline } from '../../contexts/OfflineContext';
import { useOnboarding } from '../onboarding';
import { humanizeError } from '../../utils/errorHandling';
import { polishStoryAndWait } from '../../utils/polishStory';
import { transcribeStoryAndWait, transcribeStorySegmentAndWait, TRANSCRIPTION_USER_FAILURE_MESSAGE } from '../../utils/storyTranscription';
import { getApiBase, getBackendBaseUrl } from '../../utils/apiBase';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import { StoryEditorPipelineStatus } from './StoryEditorPipelineStatus';
import { ArchiveSubjectIdentity } from '../archive/ArchiveSubjectIdentity';
import { VideoLinkAttach } from '../media/VideoLinkAttach';
import { VideoLinkCard } from '../media/VideoLinkCard';
import { hasVideoLink } from '../../utils/videoLink';
import { getStoryCoverUrl } from '../../utils/storyDisplay';
import { preflightStoryCoverFile, CUSTOMER_IMAGE_FORMATS_LINE } from '../../utils/storyCoverPreflight';
import { CUSTOMER_IMAGE_ACCEPT, IMAGE_PREVIEW_UNSUPPORTED_HINT } from '../../utils/imageUploadValidation';
import { personHref } from '../../utils/prettyArchiveLinks';
import { HelpContextLink } from '../help';
import { SoftUpgradePrompt } from '../billing/SoftUpgradePrompt';
import { useUsageLimits } from '../../hooks/useUsageLimits';
import { shouldShowFamilyArchiveVoicePrompt } from '../../utils/usageLimits';

const API = getApiBase();
const BACKEND_URL = getBackendBaseUrl();

function transcribeProgressLabel(phase) {
  switch (phase) {
    case 'starting':
      return 'Starting…';
    case 'queued':
      return 'Queued…';
    case 'transcribing':
      return 'Transcribing…';
    default:
      return 'Transcribing…';
  }
}
// Animation durations for consistency
const TRANSITION_DURATION = 0.3;

const MAX_RECORDING_SECONDS = 600;
const MAX_RECORDING_LABEL = '10:00';
const RECORDING_WARNING_SECONDS = 540;
const MAX_UPLOAD_SIZE_MB = 50;
const RECORDING_LIMIT_HINT =
  'Record up to 10 minutes. You can add another voice note afterwards if you remember more.';
const RECORDING_LIMIT_WARNING =
  'You have 1 minute left. This voice note will save automatically at 10 minutes.';
const VOICE_NOTE_CONTINUE_HELPER =
  'Add another voice note without replacing what you\'ve already preserved.';
const AUTO_SAVE_SUCCESS_MESSAGE =
  'Voice note saved. You can continue this memory with another recording.';

/** Status badge for a saved voice note (display only). */
function getVoiceNoteStatusBadge(segment) {
  const st = segment?.transcription_status;
  if (st === 'failed') {
    return {
      label: 'Needs retry',
      className:
        'border-[hsl(12_28%_88%)]/80 bg-[hsl(12_32%_97%)] text-[hsl(18_22%_38%)] dark:border-rose-900/35 dark:bg-rose-950/30 dark:text-rose-200/90',
    };
  }
  if (st === 'completed') {
    return {
      label: 'Transcribed',
      className:
        'border-[hsl(88_18%_82%)]/90 bg-[hsl(88_24%_96%)] text-[hsl(88_18%_28%)] dark:border-emerald-900/35 dark:bg-emerald-950/25 dark:text-emerald-200/85',
    };
  }
  if (st === 'processing' || st === 'queued' || st === 'pending') {
    return {
      label: 'Transcribing',
      className:
        'border-[hsl(38_22%_84%)]/90 bg-[hsl(42_30%_96%)] text-[hsl(32_18%_32%)] dark:border-amber-900/30 dark:bg-amber-950/25 dark:text-amber-100/85',
      pulse: true,
    };
  }
  return {
    label: 'Preserved',
    className:
      'border-[hsl(38_14%_86%)] bg-[hsl(42_22%_97%)] text-[hsl(28_12%_38%)] dark:border-stone-700 dark:bg-stone-900/50 dark:text-stone-300',
  };
}

function VoiceNoteStatusBadge({ segment, isActivelyTranscribing = false }) {
  const st = segment?.transcription_status;
  const showTranscribing =
    isActivelyTranscribing && st !== 'completed' && st !== 'failed';
  const badge = showTranscribing
    ? getVoiceNoteStatusBadge({ ...segment, transcription_status: 'processing' })
    : getVoiceNoteStatusBadge(segment);
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium tracking-wide ${badge.className}`}
      data-testid={`voice-note-status-${badge.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {badge.pulse ? (
        <span className="relative flex h-1.5 w-1.5 shrink-0">
          <span className="motion-safe:animate-ping absolute inline-flex h-full w-full rounded-full bg-[#8B7355]/60 opacity-75" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#8B7355]" />
        </span>
      ) : null}
      {badge.label}
    </span>
  );
}

// Format seconds to MM:SS (zero-padded)
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Calculate estimated reading time (average 200 words per minute)
const getReadingTime = (text) => {
  if (!text) return 0;
  const words = text.trim().split(/\s+/).filter(word => word.length > 0).length;
  return Math.max(1, Math.ceil(words / 200));
};

// Format text with proper paragraphs for Read Mode
const formatNarrativeText = (text) => {
  if (!text) return [];
  return text.split(/\n\n+/).filter(p => p.trim().length > 0);
};

// ============ LEGACY WRITING STUDIO ============
export const LegacyWritingStudio = ({ 
  story, 
  onUpdate, 
  onDelete, 
  canEdit = false,
  chapterInfo = null, // { current: 2, total: 4 }
  onNavigateChapter = null, // (direction: 'prev' | 'next') => void
  onFirstMemoryMilestone = null,
  /** Person this story belongs to (avatar + name); reused pattern for timelines / shared archive later. */
  archiveSubject = null,
  /** When true, sits inside StoriesTab workspace (no outer card chrome). */
  workspaceEmbedded = false,
}) => {
  const { isOnline } = useOffline();
  const { markFirstStoryRecorded } = useOnboarding();
  const canEditOnline = canEdit && isOnline;
  const { snapshot: usageSnapshot } = useUsageLimits({
    treeId: story.tree_id,
    enabled: Boolean(story.tree_id),
  });
  
  // ============ CORE STATE ============
  const [mode, setMode] = useState('write'); // 'write' | 'read'
  const [isFocusMode, setIsFocusMode] = useState(false);
  const [showPreservedMessage, setShowPreservedMessage] = useState(false);
  
  // Recording state machine
  const [recordingState, setRecordingState] = useState('inactive');
  const [recordingTime, setRecordingTime] = useState(0);
  const [micPermission, setMicPermission] = useState('ready');
  
  // Playback states
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackTime, setPlaybackTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(story.duration_seconds || 0);
  const [audioUrl, setAudioUrl] = useState(
    story.audio_file_url ? resolveAssetUrl(BACKEND_URL, story.audio_file_url) : null
  );
  
  // Transcript states
  const [transcript, setTranscript] = useState(() => {
    // Try to restore draft from localStorage first
    const savedDraft = localStorage.getItem(`story-draft-${story.id}`);
    if (savedDraft && savedDraft !== story.raw_transcript) {
      return savedDraft;
    }
    return story.raw_transcript || '';
  });
  const [lastSavedTranscript, setLastSavedTranscript] = useState(story.raw_transcript || '');
  const [transcriptSource, setTranscriptSource] = useState(story.transcript_source || null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [draftSavedNotice, setDraftSavedNotice] = useState(false);
  const [hasDraftRestored, setHasDraftRestored] = useState(() => {
    const savedDraft = localStorage.getItem(`story-draft-${story.id}`);
    return savedDraft && savedDraft !== story.raw_transcript;
  });
  
  // Polish states
  const [isPolishing, setIsPolishing] = useState(false);
  const [showPolishModal, setShowPolishModal] = useState(false);
  const [polishedTranscript, setPolishedTranscript] = useState(story.polished_transcript || '');
  const [pendingPolish, setPendingPolish] = useState('');
  
  // AI Nudging
  const [isDeepening, setIsDeepening] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState(
    () => localStorage.getItem(`nudge-${story.id}`) === 'dismissed'
  );
  
  // Transcription
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionPhase, setTranscriptionPhase] = useState('idle');
  const [transcriptionError, setTranscriptionError] = useState(null);
  const [transcriptionCompletedFlash, setTranscriptionCompletedFlash] = useState(false);
  
  // File upload
  const [isUploading, setIsUploading] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState(null);
  const [coverLightboxOpen, setCoverLightboxOpen] = useState(false);
  const [mediaStory, setMediaStory] = useState(story);

  /** Voice notes returned from API (multiple segments per story). */
  const [audioSegments, setAudioSegments] = useState(() => story.audio_segments || []);
  /** When true, show recorder to append another voice note without replacing the first. */
  const [continueRecording, setContinueRecording] = useState(false);
  const [playingSegmentId, setPlayingSegmentId] = useState(null);
  const [segmentPlaybackPaused, setSegmentPlaybackPaused] = useState(false);
  const [removingSegmentId, setRemovingSegmentId] = useState(null);
  const segmentAudioRef = useRef(null);

  // Title + manual family mentions (story_mentions API)
  const [localDisplayTitle, setLocalDisplayTitle] = useState(() => (story.display_title || '').trim());
  const [savingTitle, setSavingTitle] = useState(false);
  const [mentions, setMentions] = useState([]);
  const [treePeople, setTreePeople] = useState([]);
  const [mentionPick, setMentionPick] = useState('');
  const [mentionsLoading, setMentionsLoading] = useState(false);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const streamRef = useRef(null);
  const shouldUploadOnStopRef = useRef(false);
  const autoStoppedAtLimitRef = useRef(false);
  const recordingTimeRef = useRef(0);
  const fileInputRef = useRef(null);
  const coverFileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const studioRef = useRef(null);

  useEffect(() => {
    setMediaStory(story);
  }, [story]);

  useEffect(() => {
    setCoverPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, [story.id, story.cover_image_url, story.cover_image_urls]);

  useEffect(() => {
    return () => {
      if (coverPreviewUrl) {
        URL.revokeObjectURL(coverPreviewUrl);
      }
    };
  }, [coverPreviewUrl]);
  
  // ============ DERIVED STATE ============
  const isRecording = recordingState === 'recording';
  const isPaused = recordingState === 'paused';
  const isProcessing = recordingState === 'processing';
  const isRecordingOrPaused = isRecording || isPaused;
  const recordingProgressPercent = Math.min(100, (recordingTime / MAX_RECORDING_SECONDS) * 100);
  const showRecordingLimitWarning =
    recordingTime >= RECORDING_WARNING_SECONDS && (isRecording || isPaused);

  const displaySegments = useMemo(() => {
    if (audioSegments.length > 0) return audioSegments;
    if (story.audio_segments?.length) return story.audio_segments;
    if (audioUrl) {
      return [
        {
          id: 'legacy-primary',
          audio_file_url: audioUrl,
          duration_seconds: audioDuration,
          transcription_status: story.transcription_status,
          is_legacy_primary: true,
        },
      ];
    }
    return [];
  }, [
    audioSegments,
    story.audio_segments,
    audioUrl,
    audioDuration,
    story.transcription_status,
  ]);

  const hasVoiceNotes = displaySegments.length > 0;

  const latestVoiceSegment = useMemo(
    () => (displaySegments.length > 0 ? displaySegments[displaySegments.length - 1] : null),
    [displaySegments]
  );

  const syncSegmentsFromStoryPayload = useCallback((payload) => {
    if (Array.isArray(payload?.audio_segments) && payload.audio_segments.length > 0) {
      setAudioSegments(payload.audio_segments);
    }
  }, []);

  const resolveTranscribeTarget = useCallback(() => {
    const candidates = [
      latestVoiceSegment,
      ...(Array.isArray(story.audio_segments) ? story.audio_segments : []),
      ...audioSegments,
    ];
    for (let i = candidates.length - 1; i >= 0; i -= 1) {
      const seg = candidates[i];
      if (!seg?.id || seg.id === 'legacy-primary' || seg.is_legacy_primary) continue;
      return { type: 'segment', segmentId: seg.id };
    }
    return { type: 'story' };
  }, [latestVoiceSegment, story.audio_segments, audioSegments]);

  /** Latest note plays inline in write mode — exclude it from the right-rail player list. */
  const railVoiceSegments = useMemo(() => {
    if (mode !== 'write' || !latestVoiceSegment) return displaySegments;
    return displaySegments.filter((seg) => seg.id !== latestVoiceSegment.id);
  }, [displaySegments, latestVoiceSegment, mode]);

  useEffect(() => {
    if (Array.isArray(story.audio_segments) && story.audio_segments.length > 0) {
      setAudioSegments(story.audio_segments);
    }
  }, [story.id, story.audio_segments]);

  useEffect(() => {
    if (isProcessing || isUploading) {
      setTranscriptionCompletedFlash(false);
    }
  }, [isProcessing, isUploading]);

  useEffect(() => {
    if (!transcriptionCompletedFlash) return undefined;
    const t = setTimeout(() => setTranscriptionCompletedFlash(false), 5000);
    return () => clearTimeout(t);
  }, [transcriptionCompletedFlash]);
  
  const getWordCount = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };
  
  const wordCount = getWordCount(transcript);
  const polishedWordCount = getWordCount(polishedTranscript);
  const readingTime = getReadingTime(polishedTranscript || transcript);
  const isShortTranscript = wordCount > 0 && wordCount < 50;
  const displayText = polishedTranscript || transcript;
  const paragraphs = formatNarrativeText(displayText);
  
  // ============ EFFECTS ============
  
  // Initialize audio element
  useEffect(() => {
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.addEventListener('timeupdate', () => setPlaybackTime(audio.currentTime));
      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        setPlaybackTime(0);
      });
      audio.addEventListener('loadedmetadata', () => {
        if (audio.duration && isFinite(audio.duration)) {
          setAudioDuration(audio.duration);
        }
      });
      audioRef.current = audio;
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [audioUrl]);
  
  // Track unsaved changes
  useEffect(() => {
    setHasUnsavedChanges(transcript !== lastSavedTranscript);
  }, [transcript, lastSavedTranscript]);
  
  // Auto-save draft to localStorage every 5 seconds
  useEffect(() => {
    if (!transcript || transcript === lastSavedTranscript) return;
    
    const autoSaveTimer = setTimeout(() => {
      localStorage.setItem(`story-draft-${story.id}`, transcript);
      setDraftSavedNotice(true);
      setTimeout(() => setDraftSavedNotice(false), 2000);
    }, 5000);
    
    return () => clearTimeout(autoSaveTimer);
  }, [transcript, lastSavedTranscript, story.id]);
  
  // Clear draft when saved to server
  useEffect(() => {
    if (transcript === lastSavedTranscript) {
      localStorage.removeItem(`story-draft-${story.id}`);
      setHasDraftRestored(false);
    }
  }, [transcript, lastSavedTranscript, story.id]);

  useEffect(() => {
    setLocalDisplayTitle((story.display_title || '').trim());
  }, [story.id, story.display_title]);

  useEffect(() => {
    if (!story.tree_id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/individuals/${story.tree_id}`);
        if (!cancelled) setTreePeople(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setTreePeople([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [story.tree_id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setMentionsLoading(true);
      try {
        const res = await axios.get(`${API}/stories/${story.id}/mentions`);
        if (!cancelled) setMentions(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setMentions([]);
      } finally {
        if (!cancelled) setMentionsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [story.id]);
  
  // Focus mode keyboard handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFocusMode) {
        setIsFocusMode(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFocusMode]);
  
  // Lock body scroll in focus mode
  useEffect(() => {
    if (isFocusMode) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isFocusMode]);
  
  // ============ RECORDING FUNCTIONS ============

  const finalizeRecordingRef = useRef(null);

  const tickRecordingTimer = useCallback(() => {
    setRecordingTime((prev) => {
      const next = prev + 1;
      if (next >= MAX_RECORDING_SECONDS) {
        recordingTimeRef.current = MAX_RECORDING_SECONDS;
        autoStoppedAtLimitRef.current = true;
        finalizeRecordingRef.current?.();
        return MAX_RECORDING_SECONDS;
      }
      recordingTimeRef.current = next;
      return next;
    });
  }, []);

  const finalizeRecording = useCallback(() => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== 'inactive') {
      setRecordingState('processing');
      if (timerRef.current) clearInterval(timerRef.current);
      shouldUploadOnStopRef.current = true;
      rec.stop();
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    }
  }, []);

  useEffect(() => {
    finalizeRecordingRef.current = finalizeRecording;
  }, [finalizeRecording]);

  const consumeAutoStopFlag = () => {
    const wasAutoStop = autoStoppedAtLimitRef.current;
    autoStoppedAtLimitRef.current = false;
    return wasAutoStop;
  };

  const showRecordingSavedFeedback = (wasAutoStop) => {
    if (wasAutoStop) {
      toast.success(AUTO_SAVE_SUCCESS_MESSAGE, { duration: 6500 });
    } else {
      toast.success('Recording saved — transcribe or add text when you are ready.');
    }
  };
  
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setMicPermission('ready');
      
      let mimeType = 'audio/webm';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/mp4';
        if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = '';
      }
      
      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };
      
      mediaRecorder.onstop = async () => {
        if (shouldUploadOnStopRef.current) {
          shouldUploadOnStopRef.current = false;
          const audioBlob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' });
          const appendMode =
            continueRecording || Boolean(audioUrl) || audioSegments.length > 0 || (story.audio_segments?.length > 0);
          if (appendMode) {
            await uploadSegmentAudio(audioBlob);
          } else {
            await uploadAudio(audioBlob);
          }
        }
      };
      
      mediaRecorder.start(1000);
      autoStoppedAtLimitRef.current = false;
      recordingTimeRef.current = 0;
      setRecordingState('recording');
      setRecordingTime(0);
      
      timerRef.current = setInterval(tickRecordingTimer, 1000);
      
    } catch (error) {
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setMicPermission('denied');
      } else {
        toast.error('Unable to access microphone. Please check your browser settings.');
      }
    }
  };
  
  const pauseRecording = useCallback(() => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state === 'recording') {
      rec.pause();
      if (timerRef.current) clearInterval(timerRef.current);
      setRecordingState('paused');
    }
  }, []);

  const resumeRecording = useCallback(() => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state === 'paused') {
      rec.resume();
      timerRef.current = setInterval(tickRecordingTimer, 1000);
      setRecordingState('recording');
    }
  }, [tickRecordingTimer]);
  
  const cancelRecording = useCallback(() => {
    shouldUploadOnStopRef.current = false;
    autoStoppedAtLimitRef.current = false;
    if (timerRef.current) clearInterval(timerRef.current);
    if (mediaRecorderRef.current?.state !== 'inactive') mediaRecorderRef.current?.stop();
    if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
    audioChunksRef.current = [];
    recordingTimeRef.current = 0;
    setRecordingState('inactive');
    setRecordingTime(0);
    setContinueRecording(false);
  }, []);
  
  const uploadAudio = async (audioBlob) => {
    const savedDuration = recordingTimeRef.current || recordingTime;
    const wasAutoStop = consumeAutoStopFlag();
    try {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('[story audio upload]', {
          url: `${API}/stories/${story.id}/audio`,
          bytes: audioBlob.size,
          mimeType: audioBlob.type || '(unknown)',
          durationSeconds: savedDuration,
        });
      }
      const formData = new FormData();
      const ext = audioBlob.type.includes('mp4') ? 'mp4' : 'webm';
      formData.append('file', audioBlob, `recording.${ext}`);
      
      const response = await axios.post(`${API}/stories/${story.id}/audio`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: savedDuration ? { duration_seconds: savedDuration } : undefined,
      });
      
      setAudioUrl(resolveAssetUrl(BACKEND_URL, response.data.url));
      await axios.put(`${API}/stories/${story.id}/duration?duration_seconds=${savedDuration}`);
      setAudioDuration(savedDuration);
      
      showRecordingSavedFeedback(wasAutoStop);
      markFirstStoryRecorded();
      onFirstMemoryMilestone?.();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(humanizeError(error) || 'Unable to save your recording. Please try again.');
    } finally {
      recordingTimeRef.current = 0;
      setRecordingState('inactive');
      setRecordingTime(0);
      setContinueRecording(false);
    }
  };

  const uploadSegmentAudio = async (audioBlob, { durationSeconds } = {}) => {
    const segmentDuration = durationSeconds ?? recordingTimeRef.current ?? recordingTime;
    const wasAutoStop = consumeAutoStopFlag();
    try {
      setIsUploading(true);
      const formData = new FormData();
      const uploadName =
        audioBlob.name ||
        (audioBlob.type.includes('mp4') || audioBlob.type.includes('m4a')
          ? 'recording.mp4'
          : audioBlob.type.includes('mpeg') || audioBlob.type.includes('mp3')
            ? 'recording.mp3'
            : audioBlob.type.includes('wav')
              ? 'recording.wav'
              : audioBlob.type.includes('ogg')
                ? 'recording.ogg'
                : 'recording.webm');
      formData.append('file', audioBlob, uploadName);

      const response = await axios.post(`${API}/stories/${story.id}/audio/segments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: segmentDuration ? { duration_seconds: segmentDuration } : undefined,
      });

      const seg = response.data.segment;
      if (seg) {
        setAudioSegments((prev) => {
          const without = prev.filter((s) => s.id !== seg.id);
          return [...without, seg].sort(
            (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
          );
        });
      }

      toast.success(
        wasAutoStop
          ? AUTO_SAVE_SUCCESS_MESSAGE
          : 'Voice note added — your earlier recording stays preserved.',
        wasAutoStop ? { duration: 6500 } : undefined
      );
      markFirstStoryRecorded();
      onFirstMemoryMilestone?.();

      if (seg?.id) {
        setIsTranscribing(true);
        setTranscriptionPhase('starting');
        setTranscriptionError(null);
        try {
          const merged = await transcribeStorySegmentAndWait(API, story.id, seg.id, {
            onPhase: (p) => setTranscriptionPhase(p),
            onPollStory: syncSegmentsFromStoryPayload,
          });
          setTranscript(merged);
          setLastSavedTranscript(merged);
          if (transcriptSource !== 'edited') {
            setTranscriptSource('voice');
          }
          setHasUnsavedChanges(false);
          setTranscriptionCompletedFlash(true);
          toast.success('Your new words were gently added to your story text.');
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.debug('[segment transcribe failed]', error?.message || error);
          }
          setTranscriptionError(TRANSCRIPTION_USER_FAILURE_MESSAGE);
        } finally {
          setIsTranscribing(false);
          setTranscriptionPhase('idle');
        }
      }

      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(humanizeError(error) || 'Unable to save this voice note. Please try again.');
    } finally {
      recordingTimeRef.current = 0;
      setIsUploading(false);
      setRecordingState('inactive');
      setRecordingTime(0);
      setContinueRecording(false);
    }
  };
  
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    const validTypes = ['audio/mp3', 'audio/mpeg', 'audio/webm', 'audio/wav', 'audio/ogg', 'audio/m4a', 'audio/mp4'];
    const isValidType = validTypes.some(type => file.type.includes(type.split('/')[1])) || 
                        file.name.match(/\.(mp3|webm|wav|ogg|m4a)$/i);
    
    if (!isValidType) {
      toast.error('This file type isn\'t supported. Please use MP3, WAV, or similar audio formats.');
      return;
    }
    
    if (file.size > MAX_UPLOAD_SIZE_MB * 1024 * 1024) {
      toast.error(
        'This audio file is too large to upload. Try a shorter recording or a smaller file.'
      );
      return;
    }

    let duration;
    try {
      duration = await new Promise((resolve) => {
        const audio = new Audio();
        audio.src = URL.createObjectURL(file);
        audio.onloadedmetadata = () => {
          const d = Math.floor(audio.duration);
          URL.revokeObjectURL(audio.src);
          resolve(d && isFinite(d) ? d : undefined);
        };
        audio.onerror = () => {
          URL.revokeObjectURL(audio.src);
          resolve(undefined);
        };
      });
    } catch {
      duration = undefined;
    }

    if (duration && duration > MAX_RECORDING_SECONDS) {
      toast.error(
        'This recording is longer than 10 minutes. Please trim it or record in shorter voice notes.'
      );
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setIsUploading(true);
    try {
      // Always save uploads as voice-note segments so transcription uses the same pipeline as recordings.
      await uploadSegmentAudio(file, { durationSeconds: duration });
    } catch (error) {
      toast.error(humanizeError(error) || 'Unable to upload your audio file. Please try again.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  
  // ============ TRANSCRIPT FUNCTIONS ============
  
  const saveTranscript = async () => {
    setIsSaving(true);
    try {
      await axios.put(`${API}/stories/${story.id}`, {
        raw_transcript: transcript,
        transcript_source: transcriptSource || 'typed'
      });
      setLastSavedTranscript(transcript);
      setHasUnsavedChanges(false);
      setShowPreservedMessage(true);
      setTimeout(() => setShowPreservedMessage(false), 3000);
      if (transcript.trim().length > 0) {
        markFirstStoryRecorded();
        onFirstMemoryMilestone?.();
      }
      if (onUpdate) onUpdate();
      toast.success('Story saved');
    } catch (error) {
      toast.error('Unable to save your story. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleTranscriptChange = (e) => {
    setTranscript(e.target.value);
    if (!transcriptSource) setTranscriptSource('typed');
    else if (transcriptSource === 'voice') setTranscriptSource('edited');
  };
  
  const togglePlayback = () => {
    if (!audioRef.current) return;
    if (isPlaying) audioRef.current.pause();
    else audioRef.current.play();
    setIsPlaying(!isPlaying);
  };
  
  const handlePolish = async () => {
    if (hasUnsavedChanges) {
      toast.error('Please save your changes first');
      return;
    }
    
    setIsPolishing(true);
    try {
      toast.info('Polishing your story… This may take a minute.', { duration: 5000 });
      const text = await polishStoryAndWait(API, story.id);
      setPendingPolish(text);
      setShowPolishModal(true);
      toast.success('Polish ready — review and accept below');
    } catch (error) {
      toast.error(
        error?.message ||
          humanizeError(error) ||
          'Unable to polish your story right now. Please try again.'
      );
    } finally {
      setIsPolishing(false);
    }
  };
  
  const acceptPolish = async () => {
    const chosen = (pendingPolish || '').trim();
    if (!chosen) {
      toast.error('Polished text is empty.');
      return;
    }
    try {
      await axios.put(`${API}/stories/${story.id}/accept-polish`, {
        polished_transcript: chosen,
      });
      setTranscript(chosen);
      setLastSavedTranscript(chosen);
      setPolishedTranscript(chosen);
      setTranscriptSource((prev) => (prev === 'voice' ? 'edited' : prev || 'typed'));
      setHasUnsavedChanges(false);
      setShowPolishModal(false);
      setPendingPolish('');
      try {
        localStorage.removeItem(`story-draft-${story.id}`);
      } catch {
        /* ignore */
      }
      toast.success('Polished version applied. You can keep editing before saving.');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Unable to apply the polished version. Please try again.');
    }
  };
  
  const handleTranscribe = async () => {
    setIsTranscribing(true);
    setTranscriptionPhase('starting');
    setTranscriptionError(null);
    setTranscriptionCompletedFlash(false);
    const target = resolveTranscribeTarget();
    try {
      toast.info('Transcription queued — this may take a minute.', { duration: 4500, id: `transcribe-start-${story.id}` });
      const transcribeOptions = {
        onPhase: (p) => setTranscriptionPhase(p),
        onPollStory: syncSegmentsFromStoryPayload,
      };
      const text =
        target.type === 'segment'
          ? await transcribeStorySegmentAndWait(API, story.id, target.segmentId, transcribeOptions)
          : await transcribeStoryAndWait(API, story.id, transcribeOptions);
      setTranscript(text);
      setLastSavedTranscript(text);
      setTranscriptSource('voice');
      setHasUnsavedChanges(false);
      setTranscriptionCompletedFlash(true);
      try {
        localStorage.removeItem(`story-draft-${story.id}`);
      } catch {
        /* ignore */
      }
      toast.success('Transcription complete — story text updated from your recording.', {
        duration: 4200,
        id: `transcribe-ok-${story.id}`,
      });
      if (onUpdate) onUpdate();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('[story transcribe failed]', error?.message || error);
      }
      setTranscriptionError(TRANSCRIPTION_USER_FAILURE_MESSAGE);
      toast.error(TRANSCRIPTION_USER_FAILURE_MESSAGE, {
        duration: 6500,
        id: `transcribe-fail-${story.id}`,
      });
      if (onUpdate) onUpdate();
    } finally {
      setIsTranscribing(false);
      setTranscriptionPhase('idle');
    }
  };
  
  const handleDeepen = async () => {
    if (!transcript || transcript.length < 10) {
      toast.error('Please write something first');
      return;
    }
    
    setIsDeepening(true);
    try {
      const response = await axios.post(`${API}/stories/${story.id}/deepen`, {
        current_text: transcript
      });
      
      if (response.data.suggestion) {
        const newText = transcript + '\n\n' + response.data.suggestion;
        setTranscript(newText);
        setTranscriptSource('edited');
        toast.success('Story deepened');
      }
    } catch (error) {
      toast.error('Unable to expand your story right now. Please try again.');
    } finally {
      setIsDeepening(false);
    }
  };
  
  const handleDelete = async () => {
    if (!window.confirm('Delete this story? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/stories/${story.id}`);
      toast.success('Story deleted');
      if (onDelete) onDelete(story.id);
    } catch (error) {
      toast.error('Unable to delete this story. Please try again.');
    }
  };
  
  const dismissNudge = () => {
    setNudgeDismissed(true);
    localStorage.setItem(`nudge-${story.id}`, 'dismissed');
  };

  const primaryMemoryTitle =
    (localDisplayTitle || '').trim() || (story.question_text || '').trim() || 'Memory';
  const promptOnlyLine = (story.question_text || '').trim();

  const persistDisplayTitle = async (nextRaw) => {
    const trimmed = (nextRaw || '').trim();
    const prev = (story.display_title || '').trim();
    if (trimmed === prev) return;
    setSavingTitle(true);
    try {
      await axios.put(`${API}/stories/${story.id}`, { display_title: trimmed || null });
      if (onUpdate) onUpdate();
    } catch {
      toast.error('Could not save title');
      setLocalDisplayTitle(prev);
    } finally {
      setSavingTitle(false);
    }
  };

  const handleTitleBlur = () => {
    if (!canEditOnline) return;
    persistDisplayTitle(localDisplayTitle);
  };

  const personLabel = (p) => {
    const gn = (p?.given_names || '').trim();
    const sn = (p?.surname || '').trim();
    return `${gn} ${sn}`.trim() || 'Person';
  };

  const mentionablePeople = treePeople.filter(
    (p) =>
      p.id &&
      p.id !== story.person_id &&
      !mentions.some((m) => m.mentioned_person_id === p.id)
  );

  const handleAddMention = async () => {
    if (!mentionPick || !canEditOnline) return;
    try {
      await axios.post(`${API}/stories/${story.id}/mentions`, {
        mentioned_person_id: mentionPick,
        mention_type: 'family',
      });
      const res = await axios.get(`${API}/stories/${story.id}/mentions`);
      setMentions(Array.isArray(res.data) ? res.data : []);
      setMentionPick('');
      toast.success('Family link saved');
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add mention');
    }
  };

  const handleRemoveMention = async (pid) => {
    if (!canEditOnline) return;
    try {
      await axios.delete(`${API}/stories/${story.id}/mentions/${pid}`);
      setMentions((m) => m.filter((x) => x.mentioned_person_id !== pid));
      toast.success('Link removed');
      if (onUpdate) onUpdate();
    } catch {
      toast.error('Could not remove mention');
    }
  };

  /** Comfortable writing viewport: modest default height, grows with content up to a cap, then scrolls. */
  const adjustTextareaHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const minPx = 220;
    const maxPx =
      typeof window !== 'undefined' ? Math.min(Math.round(window.innerHeight * 0.5), 520) : 520;
    el.style.minHeight = `${minPx}px`;
    el.style.maxHeight = `${maxPx}px`;
    el.style.overflowY = 'auto';
    el.style.height = 'auto';
    const next = Math.min(maxPx, Math.max(minPx, el.scrollHeight));
    el.style.height = `${next}px`;
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [transcript, mode, isFocusMode, adjustTextareaHeight]);

  const toggleSegmentPlayback = (segment) => {
    const url = segment.audio_file_url ? resolveAssetUrl(BACKEND_URL, segment.audio_file_url) : null;
    if (!url) return;
    if (playingSegmentId === segment.id && segmentAudioRef.current) {
      if (segmentAudioRef.current.paused) {
        segmentAudioRef.current.play();
        setSegmentPlaybackPaused(false);
      } else {
        segmentAudioRef.current.pause();
        setSegmentPlaybackPaused(true);
      }
      return;
    }
    if (segmentAudioRef.current) {
      segmentAudioRef.current.pause();
      segmentAudioRef.current = null;
    }
    const audio = new Audio(url);
    segmentAudioRef.current = audio;
    audio.addEventListener('play', () => setSegmentPlaybackPaused(false));
    audio.addEventListener('pause', () => setSegmentPlaybackPaused(true));
    audio.addEventListener('ended', () => {
      setPlayingSegmentId(null);
      setSegmentPlaybackPaused(false);
      segmentAudioRef.current = null;
    });
    setPlayingSegmentId(segment.id);
    setSegmentPlaybackPaused(false);
    audio.play().catch(() => {
      toast.error('Unable to play this recording.');
      setPlayingSegmentId(null);
      setSegmentPlaybackPaused(false);
      segmentAudioRef.current = null;
    });
  };

  const isSegmentPlaying = useCallback(
    (segmentId) => playingSegmentId === segmentId && !segmentPlaybackPaused,
    [playingSegmentId, segmentPlaybackPaused],
  );

  const handleRemoveVoiceSegment = async (segment) => {
    if (!canEditOnline || !segment?.id) return;
    const ok = window.confirm(
      'Remove this voice note?\n\nThis will remove the attached audio from this story/chapter. You can record or upload another one afterwards.',
    );
    if (!ok) return;

    setRemovingSegmentId(segment.id);
    try {
      if (playingSegmentId === segment.id && segmentAudioRef.current) {
        segmentAudioRef.current.pause();
        segmentAudioRef.current = null;
        setPlayingSegmentId(null);
        setSegmentPlaybackPaused(false);
      }
      await axios.delete(`${API}/stories/${story.id}/audio/segments/${segment.id}`);
      setAudioSegments((prev) => prev.filter((s) => s.id !== segment.id));
      toast.success('Voice note removed.');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(humanizeError(error) || 'Unable to remove this voice note.');
    } finally {
      setRemovingSegmentId(null);
    }
  };

  const renderLatestVoicePlaybackStrip = () => {
    if (!hasVoiceNotes || mode !== 'write' || !latestVoiceSegment) return null;

    const seg = latestVoiceSegment;
    const isPlayingLatest = isSegmentPlaying(seg.id);
    const hasDuration = seg.duration_seconds != null && seg.duration_seconds > 0;
    const noteCount = displaySegments.length;
    const activelyTranscribing = isTranscribing && noteCount > 0;

    return (
      <div
        className="flex flex-wrap items-center gap-3 rounded-xl border border-[hsl(38_14%_86%)]/70 bg-[hsl(42_28%_99%)]/75 px-3 py-2.5 sm:px-4"
        data-testid="latest-voice-playback-strip"
      >
        <button
          type="button"
          onClick={() => toggleSegmentPlayback(seg)}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#8B7355] text-white shadow-sm transition-colors hover:bg-[#7A6548] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355]/40 focus-visible:ring-offset-2"
          aria-label={isPlayingLatest ? 'Pause latest voice note' : 'Play latest voice note'}
          data-testid="play-latest-voice-strip-btn"
        >
          {isPlayingLatest ? (
            <Pause className="h-4 w-4" fill="currentColor" aria-hidden />
          ) : (
            <Play className="h-4 w-4 ml-0.5" fill="currentColor" aria-hidden />
          )}
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-foreground/85">
            {noteCount === 1 ? '1 voice note attached' : `${noteCount} voice notes attached`}
          </p>
          <div className="mt-0.5 flex flex-wrap items-center gap-2">
            {hasDuration ? (
              <span className="inline-flex items-center gap-1 text-[11px] tabular-nums text-muted-foreground">
                <Clock className="h-3 w-3 opacity-60" aria-hidden />
                {formatTime(seg.duration_seconds)}
              </span>
            ) : null}
            <VoiceNoteStatusBadge segment={seg} isActivelyTranscribing={activelyTranscribing} />
          </div>
        </div>
        <p className="hidden text-[11px] text-muted-foreground sm:block">Latest note</p>
      </div>
    );
  };

  const renderVoiceNoteCard = (segment, index) => {
    const isPlayingSeg = isSegmentPlaying(segment.id);
    const hasDuration = segment.duration_seconds != null && segment.duration_seconds > 0;
    const isLatestNote = index === displaySegments.length - 1;
    const activelyTranscribing = isTranscribing && isLatestNote;

    return (
      <article
        key={segment.id}
        className="group rounded-2xl border border-[hsl(38_14%_88%)]/95 bg-gradient-to-br from-[hsl(42_30%_99%)] to-[hsl(40_22%_96%)]/90 p-4 shadow-[0_1px_2px_hsl(28_12%_18%/_0.04),inset_0_1px_0_hsl(42_40%_100%/0.5)] transition-shadow hover:shadow-[0_4px_14px_hsl(28_12%_18%/_0.06)] dark:border-stone-700/80 dark:from-stone-900/60 dark:to-stone-950/40"
        data-testid={`voice-segment-${segment.id}`}
      >
        <header className="flex flex-wrap items-start justify-between gap-2 gap-y-1.5 mb-3">
          <div className="min-w-0">
            <p className="font-serif text-sm font-medium text-[hsl(24_12%_22%)] dark:text-stone-100">
              Voice note {index + 1}
            </p>
            {hasDuration ? (
              <p className="mt-0.5 flex items-center gap-1.5 text-xs tabular-nums text-muted-foreground">
                <Clock className="w-3 h-3 shrink-0 opacity-60" aria-hidden />
                {formatTime(segment.duration_seconds)}
              </p>
            ) : (
              <p className="mt-0.5 text-xs text-muted-foreground/80">Your recording is kept here</p>
            )}
          </div>
          <VoiceNoteStatusBadge segment={segment} isActivelyTranscribing={activelyTranscribing} />
          {canEditOnline ? (
            <button
              type="button"
              onClick={() => handleRemoveVoiceSegment(segment)}
              disabled={removingSegmentId === segment.id}
              className="shrink-0 rounded-lg p-1.5 text-muted-foreground/60 transition-colors hover:bg-red-50 hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300/40 disabled:opacity-50"
              aria-label="Remove voice note"
              data-testid={`remove-voice-segment-${segment.id}`}
            >
              {removingSegmentId === segment.id ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Trash2 className="h-4 w-4" aria-hidden />
              )}
            </button>
          ) : null}
        </header>

        <div className="flex items-center gap-4 rounded-xl border border-[hsl(38_14%_90%)]/70 bg-[hsl(42_28%_100%)]/70 px-4 py-3 dark:border-stone-700/60 dark:bg-stone-900/35">
          <button
            type="button"
            onClick={() => toggleSegmentPlayback(segment)}
            className="h-12 w-12 shrink-0 rounded-full bg-[#8B7355] text-white flex items-center justify-center hover:bg-[#7A6548] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355]/40 focus-visible:ring-offset-2 transition-colors shadow-[0_2px_8px_hsl(28_20%_22%/_0.12)]"
            aria-label={isPlayingSeg ? 'Pause voice note' : 'Play voice note'}
          >
            {isPlayingSeg ? (
              <Pause className="w-5 h-5" fill="currentColor" />
            ) : (
              <Play className="w-5 h-5 ml-0.5" fill="currentColor" />
            )}
          </button>
          <div className="min-w-0 flex-1 space-y-0.5">
            <p className="text-sm text-[hsl(28_10%_32%)] dark:text-stone-200">
              {isPlayingSeg ? 'Playing…' : segmentPlaybackPaused && playingSegmentId === segment.id ? 'Paused' : 'Listen to this note'}
            </p>
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              Kept privately with this memory. Only your family space can hear it.
            </p>
          </div>
          <Volume2 className="w-4 h-4 shrink-0 text-[#8B7355]/35 dark:text-[#c4a574]/40" aria-hidden />
        </div>
      </article>
    );
  };

  const renderVoiceNoteRailSummary = (segment) => {
    if (!segment) return null;
    const hasDuration = segment.duration_seconds != null && segment.duration_seconds > 0;
    const activelyTranscribing = isTranscribing && segment.id === latestVoiceSegment?.id;

    return (
      <div
        className="rounded-xl border border-[hsl(38_14%_88%)]/80 bg-[hsl(42_28%_100%)]/60 px-4 py-3"
        data-testid="voice-note-rail-summary"
      >
        <p className="font-serif text-sm text-foreground/85">Latest voice note</p>
        <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
          Use the player near the editor to listen. This note is kept privately with your memory.
        </p>
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          {hasDuration ? (
            <span className="inline-flex items-center gap-1 text-[11px] tabular-nums text-muted-foreground">
              <Clock className="h-3 w-3 opacity-60" aria-hidden />
              {formatTime(segment.duration_seconds)}
            </span>
          ) : null}
          <VoiceNoteStatusBadge segment={segment} isActivelyTranscribing={activelyTranscribing} />
        </div>
      </div>
    );
  };

  const handleStartVoiceNote = () => {
    if (!canEditOnline || isRecordingOrPaused) return;
    if (hasVoiceNotes) {
      setContinueRecording(true);
      return;
    }
    startRecording();
  };

  const showTranscribeAction = canEditOnline && hasVoiceNotes;

  const showInlineRecorder =
    mode === 'write' &&
    canEditOnline &&
    (recordingState !== 'inactive' || isProcessing || continueRecording);

  const storyCoverUrl = coverPreviewUrl || getStoryCoverUrl(story, BACKEND_URL);
  const hasStoryCover = Boolean(storyCoverUrl);

  const handleCoverFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !canEditOnline) return;

    const check = await preflightStoryCoverFile(file);
    if (!check.ok) {
      toast.error(check.error || 'Could not use that photo');
      return;
    }
    if (check.previewUnsupported) {
      toast.message(check.previewHint || IMAGE_PREVIEW_UNSUPPORTED_HINT);
    }

    if (coverPreviewUrl) {
      URL.revokeObjectURL(coverPreviewUrl);
    }
    setCoverPreviewUrl(URL.createObjectURL(file));
    setCoverUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/stories/${story.id}/cover-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Photo attached to this memory');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(humanizeError(error) || 'Unable to upload photo. Please try again.');
      setCoverPreviewUrl(null);
    } finally {
      setCoverUploading(false);
    }
  };

  const handleRemoveCover = async () => {
    if (!canEditOnline || !hasStoryCover) return;
    if (!window.confirm('Remove the photo from this memory?')) return;
    setCoverUploading(true);
    try {
      await axios.delete(`${API}/stories/${story.id}/cover-image`);
      if (coverPreviewUrl) {
        URL.revokeObjectURL(coverPreviewUrl);
      }
      setCoverPreviewUrl(null);
      toast.success('Photo removed');
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(humanizeError(error) || 'Unable to remove photo');
    } finally {
      setCoverUploading(false);
    }
  };

  const handleMediaUpdated = (patch) => {
    if (patch) {
      setMediaStory((prev) => ({ ...prev, ...patch }));
    }
    if (onUpdate) onUpdate();
  };

  // ============ RENDER ============

  const renderStoryMediaPanel = () => {
    if (!canEditOnline && !hasStoryCover && !hasVideoLink(mediaStory)) {
      return null;
    }

    return (
      <div
        className="rounded-2xl border border-[hsl(38_14%_86%)] bg-[hsl(42_26%_99%)]/95 p-5 shadow-[inset_0_1px_0_hsl(42_40%_100%/0.45)] dark:border-secondary dark:bg-stone-950/40"
        data-testid="story-media-panel"
      >
        <div className="mb-4 space-y-1.5">
          <p className="font-serif text-base font-medium text-[hsl(24_12%_22%)] dark:text-stone-100">
            Photos &amp; video
          </p>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Optional — attach a cover photo or link a video alongside your written words and voice notes.
          </p>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-[hsl(38_14%_88%)]/60 bg-white/40 p-4 dark:border-stone-700/60 dark:bg-stone-900/20">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <ImageIcon className="h-4 w-4 text-[#8B7355]" aria-hidden />
                <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                  Cover photo
                </p>
              </div>
              {coverUploading ? (
                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  Uploading…
                </span>
              ) : null}
            </div>

            {hasStoryCover ? (
              <div className="space-y-3">
                <div className="overflow-hidden rounded-xl border border-[hsl(38_14%_86%)]/80">
                  <img
                    src={storyCoverUrl}
                    alt={story.cover_image_alt || 'Memory cover photo'}
                    className="aspect-[16/10] w-full object-cover"
                    data-testid="story-cover-preview"
                  />
                </div>
                {story.cover_image_caption ? (
                  <p className="text-xs text-muted-foreground">{story.cover_image_caption}</p>
                ) : null}
                {canEditOnline ? (
                  <div className="flex flex-wrap gap-2">
                    <input
                      ref={coverFileInputRef}
                      type="file"
                      accept={CUSTOMER_IMAGE_ACCEPT}
                      className="hidden"
                      id={`cover-upload-${story.id}`}
                      onChange={handleCoverFileChange}
                      data-testid="story-cover-file-input"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      disabled={coverUploading}
                      asChild
                    >
                      <label htmlFor={`cover-upload-${story.id}`} className="cursor-pointer">
                        <Upload className="mr-2 h-4 w-4" aria-hidden />
                        Replace photo
                      </label>
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="rounded-full text-muted-foreground hover:text-destructive"
                      disabled={coverUploading}
                      onClick={handleRemoveCover}
                      data-testid="story-cover-remove-btn"
                    >
                      <Trash2 className="mr-2 h-4 w-4" aria-hidden />
                      Remove
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : canEditOnline ? (
              <div className="space-y-2">
                <input
                  ref={coverFileInputRef}
                  type="file"
                  accept={CUSTOMER_IMAGE_ACCEPT}
                  className="hidden"
                  id={`cover-upload-empty-${story.id}`}
                  onChange={handleCoverFileChange}
                  data-testid="story-cover-file-input"
                />
                <Button
                  type="button"
                  size="sm"
                  className="rounded-full bg-[#8B7355] hover:bg-[#7A6548] text-white"
                  disabled={coverUploading}
                  asChild
                >
                  <label htmlFor={`cover-upload-empty-${story.id}`} className="cursor-pointer">
                    <ImageIcon className="mr-2 h-4 w-4" aria-hidden />
                    Attach a photo
                  </label>
                </Button>
                <p className="text-[11px] text-muted-foreground">{CUSTOMER_IMAGE_FORMATS_LINE}, up to 30 MB</p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">A cover photo can be added whenever you like.</p>
            )}
          </div>

          {(canEditOnline || hasVideoLink(mediaStory)) ? (
            <VideoLinkAttach
              entity={mediaStory}
              entityLabel="memory"
              canEdit={canEditOnline}
              apiBase={API}
              putPath={`/stories/${story.id}/video-link`}
              deletePath={`/stories/${story.id}/video-link`}
              onUpdated={handleMediaUpdated}
            />
          ) : null}
        </div>
      </div>
    );
  };

  const renderVoiceRecorderControls = () => (
    <div className="space-y-3" data-testid="voice-recorder-controls">
      {continueRecording && recordingState === 'inactive' && (
        <p className="text-sm font-medium text-[#8B7355]/95 dark:text-[#c4a574]">Add another voice note</p>
      )}
      {recordingState === 'inactive' && (
        <p className="text-xs leading-relaxed text-muted-foreground" data-testid="recording-limit-hint">
          {RECORDING_LIMIT_HINT}
        </p>
      )}
      {recordingState === 'inactive' && micPermission !== 'denied' && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            onClick={startRecording}
            className="rounded-full bg-[#8B7355] hover:bg-[#7A6548] text-white gap-2"
            data-testid="record-btn"
          >
            <Mic className="w-4 h-4" aria-hidden />
            {continueRecording ? 'Record next note' : 'Record voice note'}
          </Button>
          <Button type="button" size="sm" variant="outline" className="rounded-full gap-2 border-[hsl(38_14%_82%)]" asChild>
            <label htmlFor={`audio-upload-${story.id}`} className="cursor-pointer">
              <Upload className="w-4 h-4" aria-hidden />
              {isUploading ? 'Uploading…' : 'Upload audio file'}
            </label>
          </Button>
          {continueRecording ? (
            <button
              type="button"
              onClick={() => setContinueRecording(false)}
              className="text-xs text-muted-foreground hover:text-[#8B7355] px-2"
            >
              Cancel
            </button>
          ) : null}
        </div>
      )}

      {recordingState === 'inactive' && micPermission === 'denied' && (
        <div className="rounded-lg border border-border/50 bg-muted/20 p-3 text-sm text-muted-foreground">
          <p className="mb-2">Microphone is blocked. You can still upload an audio file.</p>
          <Button type="button" size="sm" className="rounded-full bg-[#8B7355] hover:bg-[#7A6548]" asChild>
            <label htmlFor={`audio-upload-${story.id}`} className="cursor-pointer gap-2">
              <Upload className="w-4 h-4" />
              Upload audio file
            </label>
          </Button>
        </div>
      )}

      {recordingState === 'recording' && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-red-200/60 bg-red-50/40 dark:border-red-900/30 dark:bg-red-950/20 p-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="h-10 w-10 shrink-0 rounded-full bg-red-500 flex items-center justify-center shadow-md motion-safe:animate-pulse">
              <div className="h-2 w-2 bg-white rounded-sm" />
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                <span className="relative flex h-2 w-2 shrink-0">
                  <span className="motion-safe:animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                </span>
                <span className="font-mono tabular-nums" data-testid="recording-timer">
                  Recording {formatTime(recordingTime)} / {MAX_RECORDING_LABEL}
                </span>
              </div>
              <div
                className="h-1 w-full max-w-md rounded-full bg-red-100/80 dark:bg-red-950/50 overflow-hidden"
                role="progressbar"
                aria-valuenow={Math.round(recordingProgressPercent)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Recording progress toward 10 minute limit"
              >
                <div
                  className="h-full rounded-full bg-[#8B7355]/75 transition-[width] duration-1000 ease-linear motion-reduce:transition-none"
                  style={{ width: `${recordingProgressPercent}%` }}
                />
              </div>
              {showRecordingLimitWarning ? (
                <p className="text-xs leading-relaxed text-amber-800/90 dark:text-amber-200/90" data-testid="recording-limit-warning">
                  {RECORDING_LIMIT_WARNING}
                </p>
              ) : (
                <p className="text-[11px] text-muted-foreground">We&apos;ll save when you stop.</p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={pauseRecording} className="rounded-full">
              <Pause className="w-4 h-4 mr-1" /> Pause
            </Button>
            <Button size="sm" onClick={finalizeRecording} className="rounded-full bg-[#8B7355] hover:bg-[#7A6548]">
              <Square className="w-4 h-4 mr-1" /> Stop
            </Button>
            <button type="button" onClick={cancelRecording} className="text-xs text-muted-foreground hover:text-red-600 px-2">
              Cancel
            </button>
          </div>
        </div>
      )}

      {recordingState === 'paused' && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-amber-200/70 bg-amber-50/35 dark:border-amber-900/35 dark:bg-amber-950/20 p-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className="h-10 w-10 shrink-0 rounded-full bg-amber-500 flex items-center justify-center shadow-md">
              <Pause className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <p className="text-sm text-amber-800 dark:text-amber-300">
                <span className="font-mono tabular-nums" data-testid="recording-timer">
                  Paused {formatTime(recordingTime)} / {MAX_RECORDING_LABEL}
                </span>
              </p>
              <div
                className="h-1 w-full max-w-md rounded-full bg-amber-100/80 dark:bg-amber-950/50 overflow-hidden"
                role="progressbar"
                aria-valuenow={Math.round(recordingProgressPercent)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Recording progress toward 10 minute limit"
              >
                <div
                  className="h-full rounded-full bg-[#8B7355]/60 transition-[width] duration-300 ease-out motion-reduce:transition-none"
                  style={{ width: `${recordingProgressPercent}%` }}
                />
              </div>
              {showRecordingLimitWarning ? (
                <p className="text-xs leading-relaxed text-amber-800/90 dark:text-amber-200/90" data-testid="recording-limit-warning">
                  {RECORDING_LIMIT_WARNING}
                </p>
              ) : (
                <p className="text-[11px] text-muted-foreground">Your recording is paused. Resume when you are ready.</p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={resumeRecording} className="rounded-full">
              <Play className="w-4 h-4 mr-1" /> Resume
            </Button>
            <Button size="sm" onClick={finalizeRecording} className="rounded-full bg-[#8B7355] hover:bg-[#7A6548]">
              <Square className="w-4 h-4 mr-1" /> Stop
            </Button>
            <button type="button" onClick={cancelRecording} className="text-xs text-muted-foreground hover:text-red-600 px-2">
              Discard
            </button>
          </div>
        </div>
      )}

      {recordingState === 'processing' && (
        <div className="flex items-center gap-3 py-1">
          <div className="h-10 w-10 shrink-0 rounded-full bg-stone-200 dark:bg-secondary flex items-center justify-center">
            <Loader2 className="w-5 h-5 text-[#8B7355] animate-spin motion-reduce:animate-none" />
          </div>
          <p className="text-sm text-muted-foreground">Uploading audio…</p>
        </div>
      )}
    </div>
  );

  const renderVoiceHistoryRail = () => {
    const showInlineLatest = mode === 'write' && Boolean(latestVoiceSegment);
    const hasRailPlayers = railVoiceSegments.length > 0;

    return (
    <div
      className="rounded-2xl border border-[hsl(38_14%_86%)] bg-[hsl(42_26%_99%)]/95 p-4 sm:p-5 shadow-[inset_0_1px_0_hsl(42_40%_100%/0.45)] dark:border-secondary dark:bg-stone-950/40"
      data-testid="story-voice-history-rail"
    >
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
        <div className="space-y-1">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Voice notes
        </p>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {!hasVoiceNotes
            ? 'Record from the editor — saved notes appear here.'
            : hasRailPlayers && showInlineLatest
              ? 'Earlier notes — latest plays from the editor.'
              : showInlineLatest
                ? 'Latest note plays from the editor above.'
                : 'Playback and transcription status for this memory.'}
        </p>
        </div>
        <HelpContextLink section="voice-recording">Recording tips</HelpContextLink>
      </div>

      {!hasVoiceNotes ? (
        <p className="rounded-xl border border-dashed border-[hsl(38_14%_84%)]/80 bg-[hsl(42_24%_98%)]/60 px-4 py-5 text-center text-xs leading-relaxed text-muted-foreground">
          Use <span className="font-medium text-foreground/75">Record voice note</span> under the writing box to capture tone and pauses alongside your words.
        </p>
      ) : hasRailPlayers ? (
        <div className="space-y-3" role="list" aria-label="Earlier voice notes">
          {railVoiceSegments.map((segment) => {
            const index = displaySegments.findIndex((s) => s.id === segment.id);
            return renderVoiceNoteCard(segment, index >= 0 ? index : 0);
          })}
        </div>
      ) : showInlineLatest ? (
        renderVoiceNoteRailSummary(latestVoiceSegment)
      ) : (
        <div className="space-y-3" role="list" aria-label="Saved voice notes">
          {displaySegments.map((segment, index) => renderVoiceNoteCard(segment, index))}
        </div>
      )}
    </div>
    );
  };

  const hasReadSupportingMedia =
    hasStoryCover ||
    hasVoiceNotes ||
    hasVideoLink(mediaStory) ||
    (!mentionsLoading && mentions.length > 0);

  const renderReadCoverThumbnail = () => {
    if (!hasStoryCover) return null;

    return (
      <>
        <div
          className="rounded-xl border border-[hsl(38_14%_88%)]/80 bg-[hsl(42_28%_100%)]/60 p-3"
          data-testid="read-cover-thumbnail"
        >
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Cover photo
          </p>
          <button
            type="button"
            onClick={() => setCoverLightboxOpen(true)}
            className="group block w-full overflow-hidden rounded-lg border border-[hsl(38_14%_86%)]/80 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355]/40 focus-visible:ring-offset-2"
            aria-label="View cover photo full size"
          >
            <img
              src={storyCoverUrl}
              alt={story.cover_image_alt || 'Memory cover photo'}
              className="aspect-[4/3] w-full max-h-40 object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            />
          </button>
          {story.cover_image_caption ? (
            <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
              {story.cover_image_caption}
            </p>
          ) : null}
        </div>
        <Dialog open={coverLightboxOpen} onOpenChange={setCoverLightboxOpen}>
          <DialogContent className="max-w-3xl border-0 bg-transparent p-2 shadow-none sm:p-3">
            <img
              src={storyCoverUrl}
              alt={story.cover_image_alt || 'Memory cover photo'}
              className="max-h-[85vh] w-full rounded-xl object-contain"
            />
          </DialogContent>
        </Dialog>
      </>
    );
  };

  const renderReadListenCard = () => {
    if (!latestVoiceSegment) return null;

    const seg = latestVoiceSegment;
    const isPlaying = isSegmentPlaying(seg.id);
    const hasDuration = seg.duration_seconds != null && seg.duration_seconds > 0;
    const noteCount = displaySegments.length;
    const activelyTranscribing = isTranscribing && noteCount > 0;

    return (
      <div
        className="rounded-xl border border-[hsl(38_14%_88%)]/80 bg-[hsl(42_28%_100%)]/60 p-3"
        data-testid="read-listen-card"
      >
        <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Listen to this memory
        </p>
        <div className="flex items-center gap-3 rounded-lg border border-[hsl(38_14%_90%)]/70 bg-[hsl(42_28%_99%)]/80 px-3 py-2.5">
          <button
            type="button"
            onClick={() => toggleSegmentPlayback(seg)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#8B7355] text-white shadow-sm transition-colors hover:bg-[#7A6548] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355]/40 focus-visible:ring-offset-2"
            aria-label={isPlaying ? 'Pause voice note' : 'Play voice note'}
            data-testid="read-listen-play-btn"
          >
            {isPlaying ? (
              <Pause className="h-4 w-4" fill="currentColor" aria-hidden />
            ) : (
              <Play className="h-4 w-4 ml-0.5" fill="currentColor" aria-hidden />
            )}
          </button>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium text-foreground/85">
              {noteCount === 1 ? 'Voice note preserved' : `${noteCount} voice notes — latest`}
            </p>
            <div className="mt-0.5 flex flex-wrap items-center gap-2">
              {hasDuration ? (
                <span className="inline-flex items-center gap-1 text-[11px] tabular-nums text-muted-foreground">
                  <Clock className="h-3 w-3 opacity-60" aria-hidden />
                  {formatTime(seg.duration_seconds)}
                </span>
              ) : null}
              <VoiceNoteStatusBadge segment={seg} isActivelyTranscribing={activelyTranscribing} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderReadEarlierVoiceNotes = () => {
    if (displaySegments.length <= 1) return null;

    const earlier = displaySegments.slice(0, -1);

    return (
      <div className="space-y-2" data-testid="read-earlier-voice-notes">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Earlier voice notes
        </p>
        {earlier.map((segment, index) => {
          const isPlayingSeg = isSegmentPlaying(segment.id);
          const hasDuration = segment.duration_seconds != null && segment.duration_seconds > 0;

          return (
            <div
              key={segment.id}
              className="flex items-center gap-2.5 rounded-lg border border-[hsl(38_14%_90%)]/70 bg-[hsl(42_28%_99%)]/70 px-3 py-2"
            >
              <button
                type="button"
                onClick={() => toggleSegmentPlayback(segment)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#8B7355]/90 text-white hover:bg-[#7A6548] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8B7355]/40"
                aria-label={isPlayingSeg ? `Pause voice note ${index + 1}` : `Play voice note ${index + 1}`}
              >
                {isPlayingSeg ? (
                  <Pause className="h-3.5 w-3.5" fill="currentColor" aria-hidden />
                ) : (
                  <Play className="h-3.5 w-3.5 ml-0.5" fill="currentColor" aria-hidden />
                )}
              </button>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-medium text-foreground/80">Note {index + 1}</p>
                {hasDuration ? (
                  <p className="text-[10px] tabular-nums text-muted-foreground">
                    {formatTime(segment.duration_seconds)}
                  </p>
                ) : null}
              </div>
              <VoiceNoteStatusBadge segment={segment} />
            </div>
          );
        })}
      </div>
    );
  };

  const renderReadVideoCard = () => {
    if (!hasVideoLink(mediaStory)) return null;

    return (
      <div data-testid="read-video-card">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Video memory
        </p>
        <VideoLinkCard entity={mediaStory} compact showExternalNote={false} />
      </div>
    );
  };

  const renderReadMentionsMetadata = () => {
    if (mentionsLoading || mentions.length === 0) return null;

    return (
      <div
        className="rounded-xl border border-[hsl(38_14%_88%)]/70 bg-[hsl(42_28%_100%)]/50 px-4 py-3"
        data-testid="read-mentions-metadata"
      >
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          <span className="font-medium text-foreground/70">Mentioned in this memory:</span>{' '}
          {mentions.map((m, index) => {
            const p = treePeople.find((x) => x.id === m.mentioned_person_id);
            const label = p ? personLabel(p) : 'Family member';
            const href = p ? personHref(p) : null;

            return (
              <span key={m.id}>
                {index > 0 ? ', ' : null}
                {href ? (
                  <Link
                    to={href}
                    className="font-medium text-[#5c4a32] underline-offset-2 hover:underline dark:text-[#c4a574]"
                  >
                    {label}
                  </Link>
                ) : (
                  <span className="font-medium text-foreground/75">{label}</span>
                )}
              </span>
            );
          })}
        </p>
      </div>
    );
  };

  const renderReadSupportingMedia = () => {
    if (!hasReadSupportingMedia) return null;

    return (
      <div className="space-y-4" data-testid="read-supporting-media">
        {renderReadCoverThumbnail()}
        {renderReadListenCard()}
        {renderReadEarlierVoiceNotes()}
        {renderReadVideoCard()}
        {renderReadMentionsMetadata()}
      </div>
    );
  };

  const studioContent = (
    <div 
      ref={studioRef}
      className={`
        transition-all duration-400 ease-out
        ${isFocusMode
          ? 'fixed inset-0 z-50 min-h-screen bg-[hsl(var(--studio-bg))] overflow-y-auto'
          : workspaceEmbedded
            ? 'min-h-0 bg-transparent border-0 shadow-none rounded-none'
            : 'min-h-screen bg-[hsl(var(--studio-bg))]/80 rounded-2xl border border-[hsl(38_14%_86%)] shadow-[0_2px_22px_hsl(28_12%_18%/_0.05)] dark:border-secondary'
        }
      `}
      data-testid="legacy-writing-studio"
    >
      {/* Studio Header */}
      <div className={`
        sticky top-0 z-10 backdrop-blur-md bg-[#FAF8F5]/90 dark:bg-stone-950/90
        border-b border-200/40 dark:border-secondary
        ${isFocusMode ? 'px-6 py-4' : 'px-5 py-4'}
      `}>
        <div className={workspaceEmbedded ? 'w-full' : 'max-w-3xl mx-auto'}>
          <div className="flex items-center justify-between">
            {/* Left: Chapter navigation */}
            <div className="flex items-center gap-3">
              {chapterInfo && onNavigateChapter && (
                <>
                  <button 
                    onClick={() => onNavigateChapter('prev')}
                    disabled={chapterInfo.current <= 1}
                    className="p-1.5 rounded-full hover:bg-stone-200/50 dark:hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    data-testid="prev-chapter-btn"
                  >
                    <ChevronLeft className="w-4 h-4 text-muted-foreground" />
                  </button>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs text-muted-foreground dark:text-muted-foreground font-light tracking-wide">
                      Story {chapterInfo.current} of {chapterInfo.total}
                    </span>
                    <span className="hidden max-w-[14rem] truncate text-[11px] font-serif text-[#5c4a32]/90 dark:text-stone-300 sm:block" title={primaryMemoryTitle}>
                      {primaryMemoryTitle}
                    </span>
                  </div>
                  <button 
                    onClick={() => onNavigateChapter('next')}
                    disabled={chapterInfo.current >= chapterInfo.total}
                    className="p-1.5 rounded-full hover:bg-stone-200/50 dark:hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    data-testid="next-chapter-btn"
                  >
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </button>
                </>
              )}
              
              {/* Progress indicator */}
              {chapterInfo && (
                <div className="hidden sm:flex items-center gap-1 ml-2">
                  {Array.from({ length: chapterInfo.total }, (_, i) => (
                    <div 
                      key={i}
                      className={`w-1.5 h-1.5 rounded-full transition-colors ${
                        i + 1 === chapterInfo.current 
                          ? 'bg-[#8B7355]' 
                          : i + 1 < chapterInfo.current 
                            ? 'bg-stone-400' 
                            : 'bg-stone-300 dark:bg-stone-700'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
            
            {/* Center: Mode Toggle */}
            <div className="flex items-center bg-stone-200/50 dark:bg-secondary rounded-full p-0.5">
              <button
                onClick={() => setMode('write')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  mode === 'write'
                    ? 'bg-white dark:bg-stone-700 text-muted-foreground dark:text-muted-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-muted-foreground dark:hover:text-muted-foreground'
                }`}
                data-testid="write-mode-btn"
              >
                <PenLine className="w-3.5 h-3.5" />
                Write
              </button>
              <button
                onClick={() => setMode('read')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  mode === 'read'
                    ? 'bg-white dark:bg-stone-700 text-muted-foreground dark:text-muted-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-muted-foreground dark:hover:text-muted-foreground'
                }`}
                data-testid="read-mode-btn"
              >
                <BookOpen className="w-3.5 h-3.5" />
                Read
              </button>
            </div>
            
            {/* Right: Focus mode toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsFocusMode(!isFocusMode)}
                className="p-2 rounded-full hover:bg-stone-200/50 dark:hover:bg-secondary transition-colors"
                title={isFocusMode ? 'Exit focus mode (Esc)' : 'Enter focus mode'}
                data-testid="focus-mode-btn"
              >
                {isFocusMode ? (
                  <Minimize2 className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <Maximize2 className="w-4 h-4 text-muted-foreground" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content Area */}
      <div className={`
        ${workspaceEmbedded ? 'w-full max-w-none' : 'max-w-[700px] mx-auto'}
        ${isFocusMode ? 'px-6 py-12' : workspaceEmbedded ? 'px-4 py-4 sm:px-5 sm:py-5 lg:px-6' : 'px-5 py-5 sm:py-6'}
      `}>
        {archiveSubject && !workspaceEmbedded && (
          <div className="mb-4 border-b border-[hsl(38_14%_88%)]/90 pb-3">
            <ArchiveSubjectIdentity person={archiveSubject} variant="compact" caption="Stories & voice" />
          </div>
        )}
        {/* Memory title + prompt (title anchors the archive entry; prompt is secondary context) */}
        <div className="mb-5 sm:mb-6 space-y-3 border-b border-[hsl(38_14%_88%)]/80 pb-5">
          <div className="space-y-2">
            {mode === 'write' ? (
              <Label htmlFor={`story-title-${story.id}`} className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                Story title
              </Label>
            ) : null}
            {mode === 'write' && canEditOnline ? (
              <Input
                id={`story-title-${story.id}`}
                value={localDisplayTitle}
                onChange={(e) => setLocalDisplayTitle(e.target.value)}
                onBlur={handleTitleBlur}
                maxLength={120}
                placeholder={promptOnlyLine ? 'Name this memory…' : 'Story title'}
                className="border-0 border-b border-[hsl(38_14%_82%)] bg-transparent px-0 py-2 text-xl font-serif text-[hsl(24_12%_18%)] shadow-none focus-visible:ring-0 focus-visible:border-[#8B7355] rounded-none dark:text-stone-100"
                data-testid="story-title-input"
              />
            ) : mode === 'write' && canEdit ? (
              <div className="space-y-1">
                <p className="text-[11px] text-amber-700 dark:text-amber-400">Reconnect to save title changes.</p>
                <Input
                  readOnly
                  value={localDisplayTitle}
                  className="border-0 border-b border-[hsl(38_14%_82%)] bg-transparent px-0 py-2 text-xl font-serif opacity-90"
                />
              </div>
            ) : (
              <h2 className="font-serif text-2xl sm:text-[1.65rem] leading-snug text-[hsl(24_12%_18%)] dark:text-stone-100">
                {primaryMemoryTitle}
              </h2>
            )}
            {savingTitle ? (
              <p className="text-[11px] text-muted-foreground">Saving title…</p>
            ) : null}
          </div>
          {promptOnlyLine ? (
            <div className="space-y-1">
              <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Prompted by</p>
              <p className="text-sm sm:text-base leading-relaxed text-muted-foreground/90 dark:text-muted-foreground/90 font-serif italic">
                &ldquo;{promptOnlyLine}&rdquo;
              </p>
            </div>
          ) : null}
        </div>
        
        {/* ============ WRITING AREA (Write Mode) ============ */}
        {mode === 'write' && canEdit && (
          <div
            className={
              workspaceEmbedded
                ? 'grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_min(20rem,30%)] xl:gap-8'
                : 'space-y-3'
            }
          >
            <div className="min-w-0 space-y-3">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground mb-1.5">Write memory</p>
            </div>
            {(isProcessing ||
              isUploading ||
              isTranscribing ||
              transcriptionCompletedFlash ||
              transcriptionError) && (
              <div className="flex flex-wrap justify-end">
                <StoryEditorPipelineStatus
                  isUploadingAudio={isProcessing || isUploading}
                  isTranscribing={isTranscribing}
                  transcriptionPhase={transcriptionPhase}
                  showTranscriptionCompleted={transcriptionCompletedFlash}
                  lastTranscriptionFailure={!isTranscribing ? transcriptionError : null}
                  onRetry={canEditOnline ? handleTranscribe : undefined}
                />
              </div>
            )}
            <Textarea
              ref={textareaRef}
              value={transcript}
              onChange={(e) => {
                handleTranscriptChange(e);
                requestAnimationFrame(() => adjustTextareaHeight());
              }}
              placeholder={"Begin writing your memory here. Take your time. There is no rush."}
              spellCheck
              lang="en-GB"
              className={`
                min-h-[220px] w-full resize-y rounded-lg border border-[hsl(38_14%_88%)]/60 bg-[hsl(42_28%_100%)]/40 px-3 py-3
                text-base sm:text-[1.0625rem] leading-relaxed placeholder:text-muted-foreground/60
                focus-visible:ring-1 focus-visible:ring-[#8B7355]/30 focus-visible:border-[#8B7355]/40
                font-sans text-[hsl(24_12%_20%)] dark:text-stone-100 dark:bg-stone-950/20
                motion-reduce:transition-none
                ${workspaceEmbedded ? 'min-h-[280px] xl:min-h-[320px]' : ''}
              `}
              style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}
              readOnly={!canEdit}
              data-testid="transcript-textarea"
            />

            {canEditOnline && showInlineRecorder ? (
              <div
                className="rounded-xl border border-[hsl(38_14%_86%)]/70 bg-[hsl(42_28%_99%)]/85 p-3 sm:p-4"
                data-testid="voice-tools-inline"
              >
                <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Voice tools
                </p>
                {renderVoiceRecorderControls()}
              </div>
            ) : null}

            {!workspaceEmbedded && (canEditOnline || hasStoryCover || hasVideoLink(mediaStory)) && renderStoryMediaPanel()}

            {/* Writing Stats */}
            <div className="flex items-center justify-between text-[11px] text-muted-foreground pt-2 border-t border-200/40 dark:border-secondary">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <Hash className="w-3 h-3" />
                  {wordCount} words
                </span>
                {wordCount > 0 && (
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {readingTime} min read
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-3">
                {/* Draft saved notice */}
                <AnimatePresence>
                  {draftSavedNotice && (
                    <motion.span 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="text-muted-foreground"
                      data-testid="draft-saved-notice"
                    >
                      Draft saved locally
                    </motion.span>
                  )}
                </AnimatePresence>
                
                {hasUnsavedChanges && !draftSavedNotice && (
                  <span className="text-amber-600 dark:text-amber-400">Unsaved changes</span>
                )}
              </div>
            </div>
            
            {/* Draft restored banner */}
            <AnimatePresence>
              {hasDraftRestored && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/40 rounded-lg flex items-center justify-between"
                  data-testid="draft-restored-banner"
                >
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Restored unsaved draft from your last session.
                  </p>
                  <button
                    onClick={() => {
                      setTranscript(story.raw_transcript || '');
                      localStorage.removeItem(`story-draft-${story.id}`);
                      setHasDraftRestored(false);
                    }}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    data-testid="discard-draft-btn"
                  >
                    Discard draft
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* AI Nudge for short transcripts */}
            <AnimatePresence>
              {isShortTranscript && !nudgeDismissed && canEdit && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="p-4 bg-[#FAF3E8] dark:bg-card border border-[#E8DFD3] dark:border-secondary rounded-lg"
                  data-testid="ai-nudge"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#8B7355]/10 flex items-center justify-center flex-shrink-0">
                      <Lightbulb className="w-4 h-4 text-[#8B7355]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground dark:text-muted-foreground">
                        That's a lovely start. Would you like to add a little more detail?
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        A few more sentences could bring this story to life.
                      </p>
                    </div>
                    <button onClick={dismissNudge} className="text-muted-foreground hover:text-muted-foreground">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {renderLatestVoicePlaybackStrip()}
            
            {canEditOnline && mode === 'write' ? (
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={handleFileUpload}
                className="hidden"
                id={`audio-upload-${story.id}`}
                data-testid="audio-file-input"
              />
            ) : null}

            {/* Primary creative actions — stay near the writing box */}
            {mode === 'write' && usageSnapshot && shouldShowFamilyArchiveVoicePrompt(usageSnapshot) ? (
              <SoftUpgradePrompt
                promptId="family-archive-voice"
                treeId={story.tree_id}
                visible
                snapshot={usageSnapshot}
                message="Voice memories and AI story polish will be part of Family Archive for families who keep building over time."
                ctaLabel="See Family Archive"
                ctaTo="/pricing#family-archive"
                featureName="family_archive_voice"
                className="mb-3"
                data-testid="soft-upgrade-prompt-voice"
              />
            ) : null}
            <div
              className="flex flex-wrap items-center gap-2 border-t border-[hsl(38_14%_88%)]/50 pt-3"
              data-testid="story-primary-actions"
            >
              <Button
                onClick={saveTranscript}
                disabled={isSaving || !hasUnsavedChanges || isRecordingOrPaused || !isOnline}
                className={`rounded-full ${hasUnsavedChanges ? 'bg-[#8B7355] hover:bg-[#7A6548]' : ''}`}
                variant={hasUnsavedChanges ? 'default' : 'outline'}
                size="sm"
                data-testid="preserve-btn"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                {isSaving ? 'Preserving...' : 'Preserve'}
              </Button>

              {canEditOnline && recordingState === 'inactive' && !continueRecording ? (
                <>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-full border-[hsl(38_14%_82%)] text-[#5c4a32] hover:bg-[hsl(42_28%_100%)]"
                    onClick={handleStartVoiceNote}
                    data-testid="record-voice-note-btn"
                  >
                    <Mic className="w-4 h-4 mr-2 opacity-80" aria-hidden />
                    Record voice note
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-full border-[hsl(38_14%_82%)] text-[#5c4a32] hover:bg-[hsl(42_28%_100%)]"
                    disabled={isUploading}
                    asChild
                  >
                    <label htmlFor={`audio-upload-${story.id}`} className="cursor-pointer">
                      <Upload className="w-4 h-4 mr-2 opacity-80" aria-hidden />
                      {isUploading ? 'Uploading…' : 'Upload audio file'}
                    </label>
                  </Button>
                </>
              ) : null}

              {canEditOnline && hasVoiceNotes && !continueRecording && recordingState === 'inactive' ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-full border-[hsl(38_14%_82%)]"
                  onClick={() => setContinueRecording(true)}
                  data-testid="continue-memory-btn"
                >
                  <Mic className="w-4 h-4 mr-2 opacity-70" aria-hidden />
                  Continue recording
                </Button>
              ) : null}

              {showTranscribeAction ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-full border-[hsl(38_14%_82%)]"
                  onClick={handleTranscribe}
                  disabled={isTranscribing}
                  data-testid="transcribe-btn"
                >
                  {isTranscribing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="w-4 h-4 mr-2 opacity-80" aria-hidden />
                  )}
                  {isTranscribing ? transcribeProgressLabel(transcriptionPhase) : 'Transcribe to text'}
                </Button>
              ) : null}

              {hasVoiceNotes && latestVoiceSegment && recordingState === 'inactive' ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-full border-[hsl(38_14%_82%)]"
                  onClick={() => toggleSegmentPlayback(latestVoiceSegment)}
                  data-testid="play-latest-voice-btn"
                >
                  {isSegmentPlaying(latestVoiceSegment.id) ? (
                    <Pause className="w-4 h-4 mr-2 opacity-80" aria-hidden />
                  ) : (
                    <Play className="w-4 h-4 mr-2 opacity-80" aria-hidden />
                  )}
                  Play latest note
                </Button>
              ) : null}

              {wordCount >= 10 && canEditOnline && (
                <Button
                  onClick={handleDeepen}
                  disabled={isDeepening || isRecordingOrPaused}
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-[#8B7355]"
                  data-testid="deepen-btn"
                >
                  {isDeepening ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Lightbulb className="w-4 h-4 mr-2" />
                  )}
                  Deepen this story
                </Button>
              )}

              {wordCount >= 20 && canEditOnline && (
                <Button
                  onClick={handlePolish}
                  disabled={isPolishing || hasUnsavedChanges || isRecordingOrPaused}
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-[#8B7355]"
                  data-testid="polish-btn"
                >
                  {isPolishing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Polish into narrative
                </Button>
              )}
            </div>

            {!workspaceEmbedded && (canEditOnline || hasVoiceNotes || audioUrl) ? (
              <div className="mt-1">{renderVoiceHistoryRail()}</div>
            ) : null}
            </div>

            {workspaceEmbedded && (canEdit || hasStoryCover || hasVideoLink(mediaStory) || hasVoiceNotes || audioUrl) ? (
              <aside className="min-w-0 space-y-4 xl:sticky xl:top-[4.5rem] xl:max-h-[calc(100vh-6rem)] xl:overflow-y-auto xl:pr-1">
                {(canEditOnline || hasStoryCover || hasVideoLink(mediaStory)) && renderStoryMediaPanel()}
                {(canEditOnline || hasVoiceNotes || audioUrl) && renderVoiceHistoryRail()}
              </aside>
            ) : null}
          </div>
        )}

        {(canEditOnline || hasStoryCover || hasVideoLink(mediaStory)) &&
          mode !== 'read' &&
          !(mode === 'write' && canEdit) && (
          <div className="mt-5">{renderStoryMediaPanel()}</div>
        )}
        {(canEditOnline || audioUrl) && mode !== 'read' && !(mode === 'write' && canEdit) && (
          <div className="mt-5">{renderVoiceHistoryRail()}</div>
        )}

        {/* Manual family mentions — write mode only */}
        {mode === 'write' && canEdit && (canEdit || mentions.length > 0) && (
          <div className="mt-6 rounded-xl border border-border/40 bg-muted/10 p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-[#8B7355]" aria-hidden />
              <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">Mention family member</p>
            </div>
            {mentionsLoading ? (
              <p className="text-xs text-muted-foreground">Loading links…</p>
            ) : mentions.length > 0 ? (
              <ul className="flex flex-wrap gap-2 mb-3">
                {mentions.map((m) => {
                  const p = treePeople.find((x) => x.id === m.mentioned_person_id);
                  const label = p ? personLabel(p) : 'Family member';
                  return (
                    <li
                      key={m.id}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(38_14%_82%)] bg-card px-3 py-1 text-xs"
                    >
                      <span>{label}</span>
                      {canEditOnline ? (
                        <button
                          type="button"
                          className="rounded-full p-0.5 text-muted-foreground hover:text-red-600"
                          onClick={() => handleRemoveMention(m.mentioned_person_id)}
                          aria-label={`Remove ${label}`}
                        >
                          <XCircle className="w-3.5 h-3.5" />
                        </button>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-xs text-muted-foreground mb-3">Link someone in this tree so the story can surface on their profile.</p>
            )}
            {canEditOnline && mentionablePeople.length > 0 ? (
              <div
                className="max-w-[34rem] rounded-xl border border-[hsl(38_14%_86%)]/55 bg-[hsl(42_28%_99%)]/60 p-3"
                data-testid="story-mention-add-row"
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-2.5">
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Add mention</Label>
                    <Select
                      value={mentionPick ? mentionPick : undefined}
                      onValueChange={(v) => setMentionPick(v)}
                    >
                      <SelectTrigger
                        className="h-9 w-full max-w-none rounded-lg bg-background"
                        data-testid="story-mention-select"
                      >
                        <SelectValue placeholder="Choose a person…" />
                      </SelectTrigger>
                      <SelectContent>
                        {mentionablePeople.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {personLabel(p)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="h-9 w-full shrink-0 rounded-full sm:w-auto"
                    disabled={!mentionPick}
                    onClick={handleAddMention}
                    data-testid="story-mention-add-btn"
                  >
                    Add link
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        )}
        
        {/* ============ READ MODE ============ */}
        {mode === 'read' && displayText && (
          <div
            className={
              hasReadSupportingMedia
                ? 'grid grid-cols-1 items-start gap-8 xl:grid-cols-[minmax(0,1fr)_min(18rem,28%)] xl:gap-10'
                : 'max-w-3xl'
            }
            data-testid="read-mode-content"
          >
            <div className="min-w-0 prose-stone">
              {paragraphs.map((paragraph, index) => (
                <p
                  key={index}
                  className={`
                    font-serif text-lg leading-[1.8] text-muted-foreground dark:text-muted-foreground
                    mb-6 last:mb-0
                    ${index === 0 ? 'first-letter:text-5xl first-letter:font-serif first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:text-[#8B7355] first-letter:leading-none' : ''}
                  `}
                  style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
                >
                  {paragraph}
                </p>
              ))}

              <div className="mt-10 flex items-center gap-4 border-t border-200/40 pt-6 text-[11px] text-muted-foreground dark:border-secondary">
                <span>{getWordCount(displayText)} words</span>
                <span>·</span>
                <span>{readingTime} min read</span>
              </div>

              {hasReadSupportingMedia ? (
                <div className="mt-8 xl:hidden">{renderReadSupportingMedia()}</div>
              ) : null}
            </div>

            {hasReadSupportingMedia ? (
              <aside className="hidden min-w-0 xl:block xl:sticky xl:top-[4.5rem] xl:max-h-[calc(100vh-6rem)] xl:overflow-y-auto xl:pr-1">
                {renderReadSupportingMedia()}
              </aside>
            ) : null}
          </div>
        )}
        
        {/* Empty state for read mode */}
        {mode === 'read' && !displayText && (
          <div className="text-center py-16">
            <p className="text-muted-foreground font-serif italic">This memory is waiting for your words.</p>
            <button 
              onClick={() => setMode('write')} 
              className="mt-4 text-sm text-[#8B7355] hover:underline"
            >
              Begin writing
            </button>
          </div>
        )}
        
        {/* Polished transcript display — only when it differs from the editor body */}
        {polishedTranscript && mode === 'write' && polishedTranscript !== transcript && (
          <div className="mt-6 pt-6 border-t border-200/40 dark:border-secondary">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-[#8B7355]" />
              <span className="text-xs uppercase tracking-widest text-[#8B7355]">Polished Narrative</span>
            </div>
            <div className="font-serif text-muted-foreground dark:text-muted-foreground leading-relaxed whitespace-pre-wrap">
              {polishedTranscript}
            </div>
          </div>
        )}
        
        {/* Delete button — write mode only */}
        {canEdit && mode === 'write' && (
          <div className="mt-8 pt-4 border-t border-200/40 dark:border-secondary flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              disabled={!isOnline}
              className="text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20"
              data-testid="delete-story-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Delete
            </Button>
          </div>
        )}
      </div>
      
      {/* Preserved Message - Emotional Reinforcement */}
      <AnimatePresence>
        {showPreservedMessage && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50"
          >
            <div className="flex items-center gap-2 px-5 py-3 bg-card dark:bg-stone-100 text-white dark:text-muted-foreground rounded-full shadow-lg">
              <Check className="w-4 h-4" />
              <span className="text-sm font-light tracking-wide">Your story is saved.</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Polish Modal */}
      <Dialog open={showPolishModal} onOpenChange={setShowPolishModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-[#FAF8F5] dark:bg-stone-950">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-serif text-muted-foreground dark:text-muted-foreground">
              <Sparkles className="w-5 h-5 text-[#8B7355]" />
              Your Story, Polished
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Does this sound like you? Edit anything before accepting.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="p-4 bg-stone-100/80 dark:bg-card rounded-lg">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Your Original</p>
              <p className="text-sm text-muted-foreground dark:text-muted-foreground leading-relaxed whitespace-pre-wrap">{transcript}</p>
            </div>
            
            <div className="p-4 bg-[#FAF3E8] dark:bg-[#8B7355]/10 border border-[#E8DFD3] dark:border-[#8B7355]/20 rounded-lg">
              <p className="text-[10px] uppercase tracking-widest text-[#8B7355] mb-2">Polished Narrative</p>
              <Textarea
                value={pendingPolish}
                onChange={(e) => setPendingPolish(e.target.value)}
                spellCheck
                lang="en-GB"
                className="min-h-[150px] resize-none bg-transparent border-0 p-0 text-muted-foreground dark:text-muted-foreground leading-relaxed focus-visible:ring-0"
                style={{ fontFamily: 'Georgia, serif' }}
                data-testid="polish-textarea"
              />
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowPolishModal(false);
                setPendingPolish('');
              }}
            >
              Keep Original
            </Button>
            <Button onClick={acceptPolish} className="bg-[#8B7355] hover:bg-[#7A6548]" data-testid="accept-polish-btn">
              <Check className="w-4 h-4 mr-2" />
              Use This Version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
  
  return studioContent;
};

export default LegacyWritingStudio;
